import { parseErrorResponse } from './error';

/**
 * Serviços de autenticação
 */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://162.240.235.102:7006';

const jsonHeaders = (token?: string) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

// ==================== INTERFACES ====================

export type Role = 'Admin' | 'Staff' | 'User';
export type Sector = 'Financeiro' | 'Comercial' | 'TI' | 'RH' | 'Operacoes';

export interface UserInfo {
  id: string;
  email: string;
  roles: Role[];
  sector: Sector;
}

export interface RegisterResponse {
  message?: string;
  userId?: string;
  email?: string;
  sector?: Sector;
  role?: Role;
  confirmationToken?: string;
}

export interface LoginResponse {
  require2fa?: boolean;
  userId?: string;
  token?: string;
  tokenType?: string;
  expiresIn?: number;
  message?: string;
  emailConfirmed?: boolean;
  user?: UserInfo;
}

export interface ConfirmEmailResponse {
  message?: string;
  success?: boolean;
}

export interface Enable2FAResponse {
  qrCode?: string;
  secret?: string;
}

export interface Confirm2FAResponse {
  recoveryCodes?: string[];
}

// ==================== AUTH FUNCTIONS ====================

/**
 * Registrar novo usuário (requer autenticação de admin)
 * POST /api/Auth/register
 */
export const registerUser = async ({ 
  email, 
  password,
  sector 
}: { 
  email: string; 
  password: string;
  sector: Sector;
}): Promise<RegisterResponse> => {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE_URL}/api/Auth/register`, {
    method: 'POST',
    headers: jsonHeaders(token || undefined),
    body: JSON.stringify({ email, password, sector }),
  });
  
  if (!res.ok) {
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Confirmar email do usuário
 * POST /api/Auth/confirm-email
 */
export const confirmEmail = async ({ 
  userId, 
  token 
}: { 
  userId: string; 
  token: string 
}): Promise<ConfirmEmailResponse> => {
  const url = `${API_BASE_URL}/api/Auth/confirm-email`;
  const body = { userId, token };
  
  // Log para debug
  console.log('confirmEmail request:', {
    url,
    userId,
    tokenLength: token.length,
    tokenPreview: token.substring(0, 30) + '...',
  });
  
  const res = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const fallback =
      res.status === 400
        ? 'Token inválido ou expirado'
        : res.status === 404
        ? 'Usuário não encontrado'
        : undefined;
    const { message, payload } = await parseErrorResponse(res, fallback);
    console.log('confirmEmail response:', {
      status: res.status,
      ok: res.ok,
      data: payload,
    });
    throw new Error(message);
  }

  const data = await res.json().catch(() => ({}));
  console.log('confirmEmail response:', {
    status: res.status,
    ok: res.ok,
    data,
  });
  return data;
};

/**
 * Login do usuário
 * POST /api/Auth/login
 */
export const loginUser = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<LoginResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/Auth/login`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    const fallback = res.status === 401 ? 'Credenciais inválidas' : undefined;
    const { message, payload } = await parseErrorResponse(res, fallback);
    if (res.status === 401) {
      const error: any = new Error(message);
      error.code = 'EMAIL_NOT_CONFIRMED';
      if (payload && typeof payload === 'object' && 'emailConfirmed' in payload) {
        error.emailConfirmed = (payload as { emailConfirmed?: boolean }).emailConfirmed;
      }
      throw error;
    }
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Login com 2FA
 * POST /api/Auth/login/2fa
 */
export const login2FA = async ({
  userId,
  twoFactorCode,
}: {
  userId: string;
  twoFactorCode: string;
}): Promise<LoginResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/Auth/login/2fa`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ userId, twoFactorCode }),
  });
  
  if (!res.ok) {
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Habilitar 2FA
 * POST /api/Auth/2fa/enable
 */
export const enable2FA = async (token: string): Promise<Enable2FAResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/Auth/2fa/enable`, {
    method: 'POST',
    headers: jsonHeaders(token),
  });
  
  if (!res.ok) {
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Confirmar 2FA
 * POST /api/Auth/2fa/confirm
 */
export const confirm2FA = async ({
  token,
  twoFactorCode,
}: {
  token: string;
  twoFactorCode: string;
}): Promise<Confirm2FAResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/Auth/2fa/confirm`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({ twoFactorCode }),
  });
  
  if (!res.ok) {
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

/**
 * Reenviar email de confirmação
 * POST /api/Auth/resend-confirmation
 */
export const resendConfirmationEmail = async ({ 
  email 
}: { 
  email: string 
}): Promise<{ message?: string }> => {
  const res = await fetch(`${API_BASE_URL}/api/Auth/resend-confirmation`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ email }),
  });
  
  if (!res.ok) {
    const { message } = await parseErrorResponse(res);
    throw new Error(message);
  }

  return res.json().catch(() => ({}));
};

// ==================== STORAGE HELPERS ====================

/**
 * Salvar dados de autenticação no localStorage
 */
export const saveAuthData = (response: LoginResponse) => {
  if (response.token) {
    localStorage.setItem('token', response.token);
  }
  if (response.expiresIn) {
    localStorage.setItem('tokenExpiry', String(Date.now() + response.expiresIn * 1000));
  }
  if (response.user) {
    localStorage.setItem('user', JSON.stringify(response.user));
  }
};

/**
 * Obter dados do usuário do localStorage
 */
export const getStoredUser = (): UserInfo | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Obter token do localStorage
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Obter data de expiração do token
 */
export const getTokenExpiry = (): number | null => {
  const expiry = localStorage.getItem('tokenExpiry');
  return expiry ? parseInt(expiry, 10) : null;
};

/**
 * Verificar se o token está expirado
 */
export const isTokenExpired = (): boolean => {
  const expiry = getTokenExpiry();
  if (!expiry) return true; // Se não há data de expiração, considerar expirado
  return Date.now() > expiry;
};

/**
 * Verificar se o token é válido (existe e não expirou)
 */
export const isTokenValid = (): boolean => {
  const token = getStoredToken();
  if (!token) return false;
  return !isTokenExpired();
};

/**
 * Limpar dados de autenticação
 */
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('tokenExpiry');
  localStorage.removeItem('user');
};

/**
 * Verificar se o usuário é admin
 */
export const isAdmin = (user: UserInfo | null): boolean => {
  return user?.roles?.includes('Admin') ?? false;
};

/**
 * Verificar se o usuário é staff
 */
export const isStaff = (user: UserInfo | null): boolean => {
  return user?.roles?.includes('Staff') ?? false;
};

/**
 * Verificar se o usuário tem acesso a uma ferramenta específica
 */
export const hasToolAccess = (user: UserInfo | null, toolSector: Sector | null): boolean => {
  if (!user) return false;
  
  // Admin tem acesso a tudo
  if (isAdmin(user)) return true;
  
  // Ferramentas comuns (sector null) são acessíveis por todos
  if (!toolSector) return true;
  
  // Staff tem acesso às ferramentas do seu setor
  if (isStaff(user) && user.sector === toolSector) return true;
  
  // User comum só tem acesso a ferramentas comuns
  return false;
};
